import {
  Component,
  inject,
  OnInit,
  DestroyRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  signal
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../shared/material/material.module';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ContainerComponent } from '../../../shared/container/container.component';
import { Address } from '../../../core/generated/model/address';
import { firstValueFrom } from 'rxjs';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { ProfileProviderPjControllerService, ProfileProviderPJ, ResourceDemand, ResourceDemandControllerService } from '../../../core/generated';
import { ResourceExamesEnum, ResourceTerapiaEnum } from '../../../shared/enums';
import { ProviderPJContextService } from '../../../shared/services/provider-pj-context.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { getEnumKey } from '../../../shared/utils/enum.util';

interface ResourceOption {
  label: string;
  key: string;
}

interface ResourceCategories {
  exames: ResourceOption[];
  terapias: ResourceOption[];
}

@Component({
  selector: 'app-resource-demand-form',
  standalone: true,
  imports: [CommonModule, MaterialModule, AvailabilityComponent, ContainerComponent],
  templateUrl: './resource-demand-form.component.html',
  styleUrls: ['./resource-demand-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourceDemandFormComponent implements OnInit {
  private readonly resourceDemandService = inject(ResourceDemandControllerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly providerPJContextService = inject(ProviderPJContextService);
  private readonly profileProviderPJService = inject(ProfileProviderPjControllerService);
  private readonly fb = inject(FormBuilder);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  providerPJId: string | null = null;
  providerPJ!: ProfileProviderPJ;
  id: string | null = null;
  addresses: Address[] = [];
  isLoading = false;

  readonly isMobile = this.deviceService.isMobile();
  readonly panelOpenState = signal(false);

  // Cache para evitar recalcular recursos repetidamente
  private readonly resourceOptionsCache = new Map<string, ResourceCategories>();

  // Lista unificada ordenada: exames (a-z) + terapias (a-z) — define a ordem dos controles do FormArray
  allResources: ResourceOption[] = [];

  // exames e terapias separados por categoria usando os enums existentes
  examResources: ResourceOption[] = [];
  therapyResources: ResourceOption[] = [];

  formResourceDemand = this.fb.group({
    id: [''],
    cnpj: [''],
    resource: this.fb.array([]),
    address: [null as Address | null],
    publicationDate: [''],
    expirationPublicationDate: [''],
    availability: this.fb.array([]),
    description: [''],
  });

  ngOnInit() {
    // Inicializar os exames e terapias ANTES de criar o FormArray
    this.initializeResourceOptions();

    this.setupContextSubscription();

    this.providerPJId =
      this.route.parent?.snapshot.paramMap.get('id') ??
      this.route.snapshot.paramMap.get('id') ??
      null;

    if (this.providerPJ) {
      this.formResourceDemand.get('cnpj')?.setValue(this.providerPJ.cnpj || null);
    }

    const routeSnapshot = this.route.snapshot;
    if (routeSnapshot.paramMap.has('id')) {
      const idParam = routeSnapshot.paramMap.get('id');
      this.id = idParam ? idParam : null;
      if (this.id !== null) {
        this.loadResourceDemand(this.id);
      }
    }

    if (this.providerPJId) {
      this.loadAddresses(this.providerPJId).then();
    }

    // Inicialize o FormArray de exames e terapias APÓS inicializar as opções
    this.formResourceDemand.setControl(
      'resource',
      this.fb.array(this.createResourceControls())
    );

    // Ensure availability FormArray is properly initialized
    const days = [21, 22, 23, 31, 32, 33, 41, 42, 43, 51, 52, 53, 61, 62, 63, 71, 72, 73, 81, 82, 83];

    const availabilityArray = this.formResourceDemand.get(
      'availability'
    ) as FormArray;
    availabilityArray.clear();

    days.forEach((availability) => {
      availabilityArray.push(this.fb.control(availability));
    });

    this.cdr.markForCheck();
  }

  private setupContextSubscription(): void {
    this.providerPJContextService.providerPJ$.pipe(
      filter((providerPJ): providerPJ is ProfileProviderPJ => providerPJ !== null),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((providerPJ) => {
      this.handleProviderPJChange(providerPJ);
    });

    this.providerPJContextService.providerId$.pipe(
      filter((id): id is string => id !== null),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((id) => {
      this.providerPJId = id;
      this.cdr.markForCheck();
    });
  }

  private handleProviderPJChange(providerPJ: ProfileProviderPJ): void {
    const hasChanged = !this.providerPJ || this.providerPJ.id !== providerPJ.id;

    if (hasChanged) {
      this.providerPJ = providerPJ;
      this.formResourceDemand.get('cnpj')?.setValue(providerPJ.cnpj || null);
      this.resourceOptionsCache.clear();
      this.cdr.markForCheck();
    }
  }

  // Inicializar as opções de exames e terapias e categorizá-las usando os enums
  private initializeResourceOptions(): void {
    const cacheKey = 'resource-categories';
    const cached = this.resourceOptionsCache.get(cacheKey);

    if (cached) {
      this.examResources = cached.exames;
      this.therapyResources = cached.terapias;
      this.allResources = [...this.examResources, ...this.therapyResources];
      return;
    }

    const exames = Object.keys(ResourceExamesEnum)
      .map((key) => ({
        key,
        label: ResourceExamesEnum[key as keyof typeof ResourceExamesEnum]
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const terapias = Object.keys(ResourceTerapiaEnum)
      .map((key) => ({
        key,
        label: ResourceTerapiaEnum[key as keyof typeof ResourceTerapiaEnum]
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.examResources = exames;
    this.therapyResources = terapias;
    this.allResources = [...exames, ...terapias];

    this.resourceOptionsCache.set(cacheKey, { exames, terapias });
  }

  // Obter o índice da oferta de exames e terapias no FormArray
  getResourceIndex(resourceKey: string): number {
    return this.allResources.findIndex(r => r.key === resourceKey);
  }

  async loadAddresses(profileId: string): Promise<void> {
    const profileProviderPJId = parseInt(profileId, 10);

    this.profileProviderPJService
      .getAllAddresses(profileProviderPJId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (addresses: Address[]) => {
          this.addresses = addresses;
          if (!this.id && this.addresses.length > 0) {
            this.formResourceDemand
              .get('address')
              ?.setValue(this.addresses[0]);
          }
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading addresses:', error);
          this.cdr.markForCheck();
        }
      });
  }

  async loadResourceDemand(id: string): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      const resourceDemand: ResourceDemand = await firstValueFrom(
        this.resourceDemandService.findById5(parseInt(id))
      );

      const { address, ...resourceDemandWithoutAddress } = resourceDemand;

      // Converter o ID number para string para compatibilidade com o formulário
      const formData = {
        ...resourceDemandWithoutAddress,
        id: resourceDemand.id?.toString() || ''
      };

      this.formResourceDemand.patchValue(formData);

      // Atualize manualmente o FormArray de availability
      const availabilityArray = this.formResourceDemand.get('availability') as FormArray;
      availabilityArray.clear();
      if (resourceDemand.availability) {
        resourceDemand.availability.forEach((availability) => {
          availabilityArray.push(this.fb.control(availability));
        });
      }

      const incomingResources: string[] = (resourceDemand.resource as any[]) ?? [];
      this.setResourceControlsFromEnumArray(incomingResources);

      if (resourceDemand.address) {
        this.setAddressInForm(resourceDemand.address);
      }

      if (resourceDemand.cnpj) {
        this.formResourceDemand.get('cnpj')?.setValue(resourceDemand.cnpj);
      }
    } catch (error) {
      console.error('Error loading resource demand:', error);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private setAddressInForm(resourceAddress: Address): void {
    const matchedAddress = this.addresses.find(
      (addr) => addr.id === resourceAddress.id
    );
    if (matchedAddress) {
      this.formResourceDemand.get('address')?.setValue(matchedAddress);
    } else {
      this.addresses.push(resourceAddress);
      this.formResourceDemand.get('address')?.setValue(resourceAddress);
    }
  }

  prepareResourceArray(): string[] {
    const arr = this.formResourceDemand.get('resource') as FormArray;
    if (!arr) return [];

    return this.allResources
      .filter((_, idx) => arr.at(idx)?.value === true)
      .map(r => r.key);
  }

  addResourceDemand(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    const value = this.formResourceDemand.value;
    const availabilityArray = this.formResourceDemand.get('availability') as FormArray;

    // Use the one that has values
    let finalAvailability: number[] = [];
    const availabilityValues1 = availabilityArray.value as number[];
    const availabilityValues2 = availabilityArray.controls.map(c => c.value) as number[];
    const availabilityValues3 = value.availability as number[];

    if (availabilityValues1 && availabilityValues1.length > 0) {
      finalAvailability = availabilityValues1;
    } else if (availabilityValues2 && availabilityValues2.length > 0) {
      finalAvailability = availabilityValues2;
    } else if (availabilityValues3 && availabilityValues3.length > 0) {
      finalAvailability = availabilityValues3;
    }

    // Definir o status apropriado
    const statusValue = ResourceDemand.StatusEnum.Close;

    const resourceDemandProviderPJ: ResourceDemand = {
      id: value.id ? parseInt(value.id) : undefined,
      cnpj: this.providerPJ.cnpj!,
      resource: this.prepareResourceArray() as any,
      address: value.address ? value.address : undefined,
      availability: finalAvailability,
      publicationDate: value.publicationDate ?? '',
      expirationPublicationDate: value.expirationPublicationDate ?? '',
      description: value.description ?? '',
      status: getEnumKey(ResourceDemand.StatusEnum, statusValue) as any,
    };

    const operation$ = this.id !== null
      ? this.resourceDemandService.update4(parseInt(this.id), resourceDemandProviderPJ)
      : this.resourceDemandService.save4(resourceDemandProviderPJ);

    operation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.router.navigate(['/providerPJ', this.providerPJId, 'resourceDemand']);
        },
        error: (error) => {
          console.error('Error saving resource demand:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date ? date >= today : false;
  };

  dateLimitFilter = (date: Date | null): boolean => {
    let publishDate = new Date();
    let today = new Date();

    if (this.formResourceDemand.controls['publicationDate'].value) {
      publishDate = new Date(
        this.formResourceDemand.controls['publicationDate'].value
      );
    } else {
      this.formResourceDemand.controls['expirationPublicationDate'].reset();
    }

    if (this.formResourceDemand.controls['publicationDate'].value) {
      today = new Date(
        this.formResourceDemand.controls['publicationDate'].value
      );
    }
    today.setHours(0, 0, 0, 0);
    return date ? date >= today : false;
  };

  get availability() {
    return this.formResourceDemand.get('availability') as FormArray;
  }

  onAvailabilityChange(newAvailability: FormArray): void {
    this.formResourceDemand.setControl('availability', newAvailability);
    this.cdr.markForCheck();
  }

  getAvailabilityValues(): number[] {
    const availabilityArray = this.formResourceDemand.get('availability') as FormArray;
    return availabilityArray ? availabilityArray.value : [];
  }

  private createResourceControls(): FormControl[] {
    return Array.from({ length: this.allResources.length }, () =>
      this.fb.control(false)
    );
  }

  get resourceArray(): FormArray {
    return this.formResourceDemand.get('resource') as FormArray;
  }

  private setResourceControlsFromEnumArray(incoming: string[]): void {
    const arr = this.formResourceDemand.get('resource') as FormArray;
    if (!arr) return;
    arr.controls.forEach((c) => c.setValue(false));

    incoming.forEach((item) => {
      const idx = this.allResources.findIndex(r => r.key === item);
      if (idx >= 0 && idx < arr.length) {
        arr.at(idx).setValue(true);
      }
    });
  }

  trackByKey(index: number, item: ResourceOption): string {
    return item?.key ?? index.toString();
  }
}
